import { APP_STORE_URL, PLAY_STORE_URL } from "@/app/constants/appStoreLinks";
import {
  INTERSTITIAL_COOKIE,
  SHOW_CLASS,
  UA_ANDROID_CLASS,
  UA_IOS_CLASS,
} from "@/app/constants/interstitial";
import { useDictionary, useUser } from "gemi/client";
import { useEffect } from "react";
import { useAuthModal } from "./AuthModalV2";
import { setAuthIntent } from "./authIntent";
import { font } from "./_shared";
import { mobileAppInterstitialDict } from "./MobileAppInterstitial.i18n";

const WEB_PREF_MAX_AGE = 60 * 60 * 24 * 180; // 180 days

function hideInterstitial() {
  document.documentElement.classList.remove(
    SHOW_CLASS,
    UA_IOS_CLASS,
    UA_ANDROID_CLASS,
  );
  document.body.style.overflow = "";
}

const downloadBtnClass =
  "w-full max-w-[360px] items-center justify-center gap-2 rounded-full bg-[#19180F] px-6 py-4 text-[16px] font-medium text-[#F7F7F2] no-underline";

function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M10 3v10m0 0l-3.5-3.5M10 13l3.5-3.5M4 16h12"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Phone-browser app-download interstitial on the landing page.
 *
 * The overlay is server-rendered but hidden by default; a pre-paint boot script
 * (see `interstitialBoot.ts`) reveals it for a phone browser before the page
 * paints, so `/` stays device-agnostic and edge-cacheable. Visibility is owned by
 * CSS via classes the boot script sets on <html> — this component never sets
 * `display`, so hydration cannot mismatch. Both store links are rendered
 * server-side and React-owned; CSS shows the one matching the boot script's
 * `folio-ua-*` class, so the CTA is tappable from first paint (no wait for JS to
 * fill an href). Shown only on a cold entry to `/`; an in-session SPA hop back to
 * `/` does not re-reveal it, which is intended (the nudge targets landing entry,
 * not engaged navigation).
 */
export default function MobileAppInterstitial() {
  const t = useDictionary(mobileAppInterstitialDict);
  const { open: openAuth } = useAuthModal();
  const { user, loading } = useUser();

  // A signed-in visitor is redirected away by Home's effect; the session cookie is
  // httpOnly so the boot script cannot know they are signed in. Hide the overlay as
  // soon as `useUser` resolves signed-in so it does not linger through the redirect.
  useEffect(() => {
    if (!loading && user) hideInterstitial();
  }, [loading, user]);

  // Insurance: clear the scroll lock / reveal if this unmounts by any path other
  // than the handlers below (e.g. a client nav away from "/"). Guarded on
  // `isConnected` so StrictMode's dev-only simulated unmount (which leaves the DOM
  // node connected) does not strip the just-added classes and blank the overlay.
  useEffect(() => {
    const el = document.getElementById("folio-app-interstitial");
    return () => {
      if (!el?.isConnected) hideInterstitial();
    };
  }, []);

  const continueOnWeb = () => {
    document.cookie = `${INTERSTITIAL_COOKIE}=1; path=/; max-age=${WEB_PREF_MAX_AGE}; SameSite=Lax; Secure`;
    hideInterstitial();
  };

  // Escape dismisses the overlay, same as "Continue on web" (its only close).
  // Attach only when the overlay is actually revealed — this component mounts for
  // every "/" visitor (desktop included; only CSS hides it), so an unconditional
  // listener would let a stray Escape on the desktop landing write the cookie.
  useEffect(() => {
    if (!document.documentElement.classList.contains(SHOW_CLASS)) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") continueOnWeb();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      id="folio-app-interstitial"
      role="dialog"
      aria-modal="true"
      aria-labelledby="folio-app-interstitial-title"
      className="fixed inset-0 z-40 flex-col overflow-y-auto overscroll-contain bg-white px-6 pb-12 pt-5"
      style={font.body}
    >
      <div className="flex justify-end">
        <button
          type="button"
          onClick={continueOnWeb}
          className="flex items-center gap-1.5 px-1 py-2 text-[14px] font-medium text-[#86848A]"
        >
          {t("continueOnWeb")}
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden
          >
            <path
              d="M4 8h8M9 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      {/* my-auto centers on a tall viewport; when content + top bar exceed the
          viewport (landscape, small height, accessibility zoom) the auto margins
          collapse and the overlay itself scrolls, so the CTA stays reachable. */}
      <div className="my-auto flex flex-col items-center gap-6 py-6 text-center">
        <img
          src="/v2/logo-mark.svg"
          alt="Folio AI"
          className="size-20 rounded-[22px] border border-[#f0b04d] bg-[#F7F7F2] p-4"
        />
        <div className="flex flex-col gap-2">
          <h2
            id="folio-app-interstitial-title"
            className="m-0 text-[32px] font-medium leading-[1.05] tracking-[-1px] text-[#343139]"
            style={font.display}
          >
            {t("title")}
          </h2>
          <p className="m-0 max-w-[320px] text-[16px] leading-[1.4] text-[#86848A]">
            {t("subtitle")}
          </p>
        </div>
        {/* Both store links are server-rendered; the boot script's folio-ua-* class
            reveals the matching one via CSS. Keeps the CTA tappable pre-hydration. */}
        <a
          id="folio-app-download-ios"
          href={APP_STORE_URL}
          className={downloadBtnClass}
        >
          <DownloadIcon />
          {t("downloadApp")}
        </a>
        <a
          id="folio-app-download-android"
          href={PLAY_STORE_URL}
          className={downloadBtnClass}
        >
          <DownloadIcon />
          {t("downloadApp")}
        </a>
        <p className="m-0 text-[14px] text-[#86848A]">
          {t("logInPrompt")}{" "}
          <button
            type="button"
            onClick={() => {
              setAuthIntent({ target: "chat" });
              openAuth("signin");
            }}
            className="font-medium text-[#343139] underline"
          >
            {t("logIn")}
          </button>
        </p>
      </div>
    </div>
  );
}
